import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import type { PanelLoadPriority } from 'app/stores/boards/BoardLoadScheduler';

export const BoardScrollRootContext = createContext<React.RefObject<HTMLElement> | null>(null);

export function computeLoadPriority(element: HTMLElement | null, root: HTMLElement | null): PanelLoadPriority {
  if (!element || !root) {
    return [Infinity, Infinity, Infinity];
  }

  const rect = element.getBoundingClientRect();
  const view = root.getBoundingClientRect();
  let distance = 0;

  if (rect.bottom < view.top) {
    distance = view.top - rect.bottom;
  } else if (rect.top > view.bottom) {
    distance = rect.top - view.bottom;
  }

  return [distance, rect.top, rect.left];
}

interface UsePanelVisibilityOptions {
  enabled: boolean;
  /** Re-creates the observer when it changes, so the current state is reported again (e.g. panel id change). */
  observeKey: string;
  onVisibilityChange: (isInView: boolean) => void;
}

export function usePanelVisibility({ enabled, observeKey, onVisibilityChange }: UsePanelVisibilityOptions) {
  const rootRef = useContext(BoardScrollRootContext);
  const elementRef = useRef<HTMLDivElement>(null);
  const onVisibilityChangeRef = useRef(onVisibilityChange);
  onVisibilityChangeRef.current = onVisibilityChange;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const element = elementRef.current;
    const root = rootRef?.current ?? null;

    if (!element || !root || typeof IntersectionObserver === 'undefined') {
      onVisibilityChangeRef.current(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        onVisibilityChangeRef.current(entries[entries.length - 1].isIntersecting);
      },
      // one root height above and below; % margins would resolve against the root width
      { root, rootMargin: `${root.clientHeight}px 0px` }
    );
    observer.observe(element);

    return () => observer.disconnect();
  }, [enabled, rootRef, observeKey]);

  const getPriority = useCallback(() => computeLoadPriority(elementRef.current, rootRef?.current ?? null), [rootRef]);

  return { elementRef, getPriority };
}
